import { useState, useEffect } from "react";
import { supabase } from '../supabase';
import { logActivity } from './ActivityLog';
import { composePortalHtml } from './portal/compose';
import { loadSections, toSectionMap } from './portal/sectionStore';
import { SectionEditorView } from './portal/SectionEditor';
import { CustomerSelect, DEFAULT_TIERS } from './Customers';
import { compressImage, EXT_BY_TYPE, makeAdoptUrl } from './imageTools';

const C = {
  bg:"#0e0e0e", sidebar:"#0a0a0a", surface:"#181818", surface2:"#202020",
  border:"#2a2a2a", muted:"#666", orange:"#FF5F1F", magenta:"#E8197D",
  gold:"#F5A623", green:"#4ade80", white:"#fff", red:"#f87171", blue:"#60a5fa",
};
const card  = (x={}) => ({ background:C.surface, borderRadius:10, padding:16, border:`0.5px solid ${C.border}`, ...x });
const inp   = (x={}) => ({ background:C.surface2, border:`0.5px solid ${C.border}`, borderRadius:8, padding:"9px 12px", color:C.white, fontSize:13, outline:"none", width:"100%", boxSizing:"border-box", ...x });
const btn   = (v="primary",x={}) => ({ padding:"8px 16px", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer", border:"none", boxSizing:"border-box", ...(v==="primary"?{background:`linear-gradient(135deg,${C.orange},${C.magenta})`,color:C.white}:{}), ...(v==="ghost"?{background:"none",border:`0.5px solid ${C.border}`,color:C.muted}:{}), ...(v==="danger"?{background:"#3d1515",color:C.red,border:`0.5px solid #f8717133`}:{}), ...x });
const lbl   = t => <div style={{fontSize:11,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:0.5}}>{t}</div>;
const secTitle = t => <div style={{fontSize:11,color:C.muted,marginBottom:12,letterSpacing:0.5,textTransform:"uppercase"}}>{t}</div>;
const gradBar = <div style={{height:2,background:`linear-gradient(90deg,${C.orange},${C.magenta},${C.gold})`,borderRadius:1,marginBottom:24}}/>;

const PORTAL_BASE = "https://app.royal.show/p/";
const BUCKET = "portals";

function Toast({ msg, ok=true }) {
  if(!msg) return null;
  return <div style={{position:"fixed",bottom:72,left:"50%",transform:"translateX(-50%)",background:C.surface,border:`0.5px solid ${ok?C.green:C.red}44`,color:ok?C.green:C.red,padding:"10px 20px",borderRadius:20,fontSize:13,zIndex:400,whiteSpace:"nowrap"}}>{msg}</div>;
}

function slugify(str) {
  return (str||"")
    .toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g,"")
    .replace(/[^a-z0-9]+/g,"-")
    .replace(/^-+|-+$/g,"");
}
function randomToken(len=4) {
  const chars = "abcdefghijkmnpqrstuvwxyz23456789";
  let out = ""; for(let i=0;i<len;i++) out += chars[Math.floor(Math.random()*chars.length)];
  return out;
}
const fmtNok = n => (Number(n)||0).toLocaleString("nb-NO").replace(/ /g," ");
const newCustomId = () => "custom-" + randomToken(5);

const DEFAULT_CONFIG = { guidePrice:"17500", discountPct:"15", contactEmail:"post@royaldancecompany.com", heroVideo:"", ctaVideo:"", volumeDiscount:false, volumeTiers:[], language:"no", heroIntro:"" };
const LANGS = [{ code:"no", label:"Norsk" }, { code:"en", label:"English" }];

// Bygg builder-items fra lagret portal-tilstand (eller default for ny portal).
// `lib` = seksjonsbiblioteket lastet fra DB.
function itemsFromStored(storedSections, lib) {
  const map = Object.fromEntries(lib.map(s => [s.key, s]));
  if(!storedSections || !storedSections.length) {
    return lib.map(s => ({ type:"section", key:s.key, label:s.label, enabled:s.enabledDefault!==false }));
  }
  const seen = new Set();
  const items = [];
  for(const it of storedSections) {
    if(it.type==="section" && map[it.key]) {
      seen.add(it.key);
      items.push({ type:"section", key:it.key, label:map[it.key].label, enabled:!!it.enabled });
    } else if(it.type==="custom") {
      items.push({ type:"custom", id:it.id||newCustomId(), title:it.title||"", eyebrow:it.eyebrow||"",
        text:it.text||"", image_url:it.image_url||"", video_url:it.video_url||"" });
    }
  }
  // Nye standardseksjoner som ikke fantes da portalen ble lagret -> legg til avslått
  for(const s of lib) if(!seen.has(s.key)) items.push({ type:"section", key:s.key, label:s.label, enabled:false });
  return items;
}

export function PortalsPage({ user, projects, customers=[], onCreateCustomer, isMobile }) {
  const [view, setView] = useState("list");      // "list" | "builder" | "sections"
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ msg:"", ok:true });
  const showToast = (msg, ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast({msg:"",ok:true}),2800); };

  // Seksjonsbibliotek (lastet fra DB, seedet fra repo)
  const [sectionsLib, setSectionsLib] = useState([]);
  const onSectionSaved = (key, patch) =>
    setSectionsLib(lib => lib.map(s => s.key===key ? { ...s, ...patch } : s));

  // Builder-state
  const [editing, setEditing] = useState(null);  // portal-rad ved redigering, ellers null
  const [customer, setCustomer] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const activeProjects = (projects||[]).filter(p => !p.archived);
  const projName = id => activeProjects.find(p => p.id === id)?.name;
  const fmtDate = d => new Date(d+"Z").toLocaleDateString("nb-NO",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"});

  useEffect(() => { loadPortals(); loadSections().then(setSectionsLib); }, []);
  const loadPortals = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('portals').select('*').order('created_at', { ascending:false });
    if(!error && data) setPortals(data);
    setLoading(false);
  };

  // src = innholdskilde (portal/null), editingRow = raden som redigeres.
  // editingRow=null med src satt = duplisering: samme innhold, ny portal.
  const openBuilder = (src=null, editingRow=null) => {
    if(!sectionsLib.length) { showToast("Laster seksjoner – prøv igjen om et øyeblikk.", false); return; }
    loadSections().then(setSectionsLib);  // hold seksjonsbiblioteket ferskt før publisering

    setEditing(editingRow);
    setCustomer(editingRow ? (src?.customer || "") : "");   // duplikat: velg ny kunde
    setCustomerId(editingRow ? (src?.customer_id || "") : "");
    setProjectId(editingRow ? (src?.project_id || "") : "");
    setConfig({ ...DEFAULT_CONFIG, ...(src?.config || {}),
      guidePrice: String(src?.config?.guidePrice ?? DEFAULT_CONFIG.guidePrice),
      discountPct: String(src?.config?.discountPct ?? DEFAULT_CONFIG.discountPct) });
    setItems(itemsFromStored(src?.sections, sectionsLib));
    setView("builder");
  };

  // ---- Builder-handlinger ----
  const setCfg = (k,v) => setConfig(c => ({ ...c, [k]:v }));
  const move = (idx, dir) => setItems(arr => {
    const j = idx+dir; if(j<0||j>=arr.length) return arr;
    const a=[...arr]; [a[idx],a[j]]=[a[j],a[idx]]; return a;
  });
  const toggleSection = idx => setItems(arr => arr.map((it,i)=> i===idx&&it.type==="section" ? {...it, enabled:!it.enabled} : it));
  const updateCustom = (idx, patch) => setItems(arr => arr.map((it,i)=> i===idx ? {...it, ...patch} : it));
  const removeItem = idx => setItems(arr => arr.filter((_,i)=>i!==idx));
  const addCustom = () => setItems(arr => [...arr, { type:"custom", id:newCustomId(), title:"", eyebrow:"", text:"", image_url:"", video_url:"", _file:null }]);

  const partnerPrice = Math.round((Number(config.guidePrice)||0) * (1 - (Number(config.discountPct)||0)/100));

  const saveBuilder = async () => {
    if(!customerId || !customer.trim()) { showToast("Velg en kunde.", false); return; }
    const enabledCount = items.filter(it => it.type==="custom" || it.enabled).length;
    if(enabledCount===0) { showToast("Velg minst én seksjon.", false); return; }
    setSaving(true);

    const slug = editing?.slug || `${slugify(customer)}-${randomToken()}`;
    const storage_path = `${slug}/index.html`;

    try {
      // 1) Last opp ventende custom-bilder til <slug>/assets/, og adopter bilder
      //    arvet fra en annen portal (via «Dupliser») inn i vår egen mappe – ellers
      //    mister duplikatet bildene den dagen originalen slettes.
      const adoptUrl = makeAdoptUrl({ storage:supabase.storage, bucket:BUCKET, slug,
        urlPrefix:"p", dir:s=>s, token:randomToken });
      const workItems = [...items];
      for(let i=0;i<workItems.length;i++){
        const it = workItems[i];
        if(it.type!=="custom") continue;
        if(it._file){
          const file = await compressImage(it._file);
          const ext = EXT_BY_TYPE[file.type] || (it._file.name.split('.').pop()||'jpg').toLowerCase().replace(/[^a-z0-9]/g,'');
          const apath = `${slug}/assets/${randomToken(6)}.${ext}`;
          const { error } = await supabase.storage.from(BUCKET).upload(apath, file, { contentType: file.type||undefined });
          if(error) throw new Error("Bilde-opplasting feilet: "+error.message);
          workItems[i] = { ...it, image_url:`/p/${slug}/assets/${apath.split('/').pop()}`, _file:null };
        } else if(it.image_url){
          workItems[i] = { ...it, image_url: await adoptUrl(it.image_url) };
        }
      }

      // 2) Komponer HTML
      const composeOrder = workItems
        .filter(it => it.type==="custom" || it.enabled)
        .map(it => it.type==="section"
          ? { type:"section", key:it.key }
          : { type:"custom", id:it.id, title:it.title, eyebrow:it.eyebrow, text:it.text, image_url:it.image_url, video_url:it.video_url });
      const cfgOut = {
        guidePrice: Number(config.guidePrice)||0,
        discountPct: Number(config.discountPct)||0,
        contactEmail: config.contactEmail || "",
        heroVideo: config.heroVideo || "",
        ctaVideo: config.ctaVideo || "",
        volumeDiscount: !!config.volumeDiscount,
        volumeTiers: Array.isArray(config.volumeTiers) ? config.volumeTiers : [],
        language: config.language==="en" ? "en" : "no",
        heroIntro: (config.heroIntro || "").trim(),
      };
      // Hent ferske seksjoner rett før komposisjon, så publisering aldri bruker
      // en utdatert mal som lå i minnet fra da backstage ble lastet.
      const freshLib = await loadSections();
      setSectionsLib(freshLib);
      const html = composePortalHtml({ order:composeOrder, sectionMap: toSectionMap(freshLib), config:{ customer:customer.trim(), ...cfgOut } });
      const blob = new Blob([html], { type:"text/html" });

      // 3) Last opp/oppdater index.html
      const store = supabase.storage.from(BUCKET);
      const up = editing
        ? await store.update(storage_path, blob, { contentType:"text/html", cacheControl:"30" })
        : await store.upload(storage_path, blob, { contentType:"text/html", cacheControl:"30" });
      if(up.error) throw new Error("Publisering feilet: "+up.error.message);

      // 4) Lagre portal-rad (tilstand for senere redigering)
      const sectionsState = workItems.map(it => it.type==="section"
        ? { type:"section", key:it.key, enabled:!!it.enabled }
        : { type:"custom", id:it.id, title:it.title, eyebrow:it.eyebrow, text:it.text, image_url:it.image_url, video_url:it.video_url });
      const customState = sectionsState.filter(s => s.type==="custom");
      const row = {
        slug, customer:customer.trim(), customer_id:customerId||null, project_id:projectId||null, storage_path,
        config:cfgOut, sections:sectionsState, custom_sections:customState,
      };
      if(editing){
        const { error } = await supabase.from('portals').update(row).eq('id', editing.id);
        if(error) throw new Error("Kunne ikke lagre: "+error.message);
      } else {
        const { error } = await supabase.from('portals').insert({ ...row, created_by:user?.id||null });
        if(error){ await store.remove([storage_path]); throw new Error("Kunne ikke lagre: "+error.message); }
      }

      logActivity(user, editing?'portal_updated':'portal_created', { customer:customer.trim(), slug }, 'portals');
      setItems(workItems);
      showToast(editing ? "Portal oppdatert! 🎉" : "Portal opprettet! 🎉");
      setSaving(false);
      setView("list");
      loadPortals();
    } catch(e){
      showToast(e.message || "Noe gikk galt.", false);
      setSaving(false);
    }
  };

  const deletePortal = async (p) => {
    if(!window.confirm(`Slette portalen for "${p.customer}"? Lenken slutter å virke.`)) return;
    // fjern hele mappen (index.html + assets)
    const { data: files } = await supabase.storage.from(BUCKET).list(p.slug, { limit:100 });
    const paths = [p.storage_path, ...(files||[]).map(f=>`${p.slug}/${f.name}`)];
    const { data: assets } = await supabase.storage.from(BUCKET).list(`${p.slug}/assets`, { limit:100 });
    (assets||[]).forEach(f=>paths.push(`${p.slug}/assets/${f.name}`));
    await supabase.storage.from(BUCKET).remove([...new Set(paths)]);
    const { error } = await supabase.from('portals').delete().eq('id', p.id);
    if(error) { showToast("Kunne ikke slette.", false); return; }
    showToast("Portal slettet.");
    setPortals(prev => prev.filter(x => x.id !== p.id));
  };

  const copyLink = (slug) => { navigator.clipboard.writeText(PORTAL_BASE+slug); showToast("Lenke kopiert!"); };

  // ============================ SEKSJONS-EDITOR ============================
  if(view==="sections") {
    return (
      <div style={{paddingBottom:isMobile?80:0}}>
        <Toast msg={toast.msg} ok={toast.ok}/>
        {gradBar}
        <SectionEditorView sections={sectionsLib} onSaved={onSectionSaved} onBack={()=>setView("list")} showToast={showToast}/>
      </div>
    );
  }

  // ============================ BUILDER ============================
  if(view==="builder") {
    return (
      <div style={{paddingBottom:isMobile?80:0,maxWidth:680}}>
        <Toast msg={toast.msg} ok={toast.ok}/>
        {gradBar}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontSize:20,fontWeight:500}}>{editing ? "Rediger portal" : "Ny kundeportal"}</div>
          <button style={{...btn("ghost"),padding:"6px 14px",fontSize:12}} onClick={()=>setView("list")}>← Tilbake</button>
        </div>

        {/* Kunde + config */}
        <div style={{...card(),marginBottom:18}}>
          {secTitle("Kunde & oppsett")}
          {lbl("Kunde *")}
          <div style={{marginBottom:14}}>
            <CustomerSelect customers={customers} value={customerId} onCreate={onCreateCustomer} required
              onChange={id=>{
                setCustomerId(id||"");
                const c = customers.find(x=>x.id===id);
                setCustomer(c?.name||"");
                if(c) setConfig(cfg=>({
                  ...cfg,
                  // ny portal arver kundens språk; en eksisterende portal bytter ikke språk i det stille
                  ...(editing ? {} : { language: c.language==="en" ? "en" : "no" }),
                  guidePrice: String(c.guide_price ?? cfg.guidePrice),
                  discountPct: String(c.partner_discount_pct ?? cfg.discountPct),
                  volumeDiscount: !!c.volume_discount,
                  volumeTiers: (c.volume_tiers && c.volume_tiers.length) ? c.volume_tiers : (c.volume_discount ? DEFAULT_TIERS : []),
                }));
              }}/>
          </div>
          {lbl("Knytt til prosjekt (valgfritt)")}
          <select style={{...inp(),marginBottom:14,appearance:"none"}} value={projectId} onChange={e=>setProjectId(e.target.value)}>
            <option value="">— Ingen —</option>
            {activeProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {lbl("Språk på portalen")}
          <div style={{display:"flex",gap:8,marginBottom:14}}>
            {LANGS.map(l => (
              <button key={l.code} type="button" onClick={()=>setCfg("language",l.code)}
                style={{...btn((config.language||"no")===l.code?"primary":"ghost"),padding:"6px 16px",fontSize:12}}>{l.label}</button>
            ))}
          </div>
          <div style={{display:"flex",gap:12,marginBottom:14,flexWrap:"wrap"}}>
            <div style={{flex:"1 1 140px"}}>{lbl("Veiledende pris (NOK)")}
              <input style={inp()} type="number" value={config.guidePrice} onChange={e=>setCfg("guidePrice",e.target.value)} /></div>
            <div style={{flex:"1 1 120px"}}>{lbl("Partner-rabatt (%)")}
              <input style={inp()} type="number" value={config.discountPct} onChange={e=>setCfg("discountPct",e.target.value)} /></div>
          </div>
          <div style={{fontSize:12,color:C.muted,marginBottom:14}}>
            {Number(config.discountPct)>0
              ? <>Partner-pris: <span style={{color:C.green}}>{fmtNok(partnerPrice)} NOK</span> per artist</>
              : <>Ingen partner-rabatt – viser kun veiledende pris.</>}
          </div>
          <div onClick={()=>setCfg("volumeDiscount",!config.volumeDiscount)} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",marginBottom:14}}>
            <span style={{width:18,height:18,borderRadius:5,border:`1.5px solid ${config.volumeDiscount?C.green:C.border}`,background:config.volumeDiscount?C.green:"transparent",color:"#0a0a0a",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center"}}>{config.volumeDiscount?"✓":""}</span>
            <span style={{fontSize:14}}>Vis volumrabatt</span>
            {config.volumeDiscount && (config.volumeTiers||[]).length>0 &&
              <span style={{fontSize:12,color:C.muted}}>({(config.volumeTiers||[]).map(t=>`${t.pct}%`).join(" / ")})</span>}
          </div>
          {lbl("Kontakt-e-post")}
          <input style={{...inp(),marginBottom:14}} value={config.contactEmail} onChange={e=>setCfg("contactEmail",e.target.value)} />
          <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
            <div style={{flex:"1 1 220px"}}>{lbl("Hero-video URL (valgfritt)")}
              <input style={inp()} placeholder="https://…/hero.mp4" value={config.heroVideo} onChange={e=>setCfg("heroVideo",e.target.value)} /></div>
            <div style={{flex:"1 1 220px"}}>{lbl("CTA-video URL (valgfritt)")}
              <input style={inp()} placeholder="tom = bruker hero" value={config.ctaVideo} onChange={e=>setCfg("ctaVideo",e.target.value)} /></div>
          </div>
          <div style={{marginTop:14}}>
            {lbl("Introtekst på forsiden (valgfritt)")}
            <textarea style={{...inp(),minHeight:76,resize:"vertical"}}
              placeholder={(() => {
                // standardteksten fra malen, på valgt språk – vises som hint
                const hero = sectionsLib.find(s=>s.key==="hero");
                const src = (config.language==="en" && hero?.html_en) ? hero.html_en : (hero?.html || "");
                const m = /<p class="hero-sub reveal">([\s\S]*?)<\/p>/.exec(src);
                return m ? m[1].replace(/<[^>]+>/g,"").trim() : "";
              })()}
              value={config.heroIntro||""} onChange={e=>setCfg("heroIntro",e.target.value)} />
            <div style={{fontSize:12,color:C.muted,marginTop:6}}>Tom = standardteksten (vist som hint) brukes.</div>
          </div>
        </div>

        {/* Seksjoner */}
        <div style={{...card(),marginBottom:18}}>
          {secTitle("Seksjoner — huk av og dra i rekkefølge")}
          {items.map((it,idx)=>(
            <div key={it.type==="custom"?it.id:it.key}
              style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 0",borderBottom:idx<items.length-1?`0.5px solid ${C.border}`:"none"}}>
              <div style={{display:"flex",flexDirection:"column",gap:2,paddingTop:2}}>
                <button onClick={()=>move(idx,-1)} disabled={idx===0} style={{...btn("ghost"),padding:"1px 7px",fontSize:11,opacity:idx===0?0.3:1}}>↑</button>
                <button onClick={()=>move(idx,1)} disabled={idx===items.length-1} style={{...btn("ghost"),padding:"1px 7px",fontSize:11,opacity:idx===items.length-1?0.3:1}}>↓</button>
              </div>
              {it.type==="section" ? (
                <div onClick={()=>toggleSection(idx)} style={{flex:1,cursor:"pointer",display:"flex",alignItems:"center",gap:10,paddingTop:3}}>
                  <span style={{width:18,height:18,borderRadius:5,border:`1.5px solid ${it.enabled?C.green:C.border}`,background:it.enabled?C.green:"transparent",color:"#0a0a0a",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{it.enabled?"✓":""}</span>
                  <span style={{fontSize:14,color:it.enabled?C.white:C.muted}}>{it.label}</span>
                  {it.key==="hero" && <span style={{fontSize:11,color:C.muted}}>(toppbilde)</span>}
                </div>
              ) : (
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                    <span style={{fontSize:11,color:C.magenta,textTransform:"uppercase",letterSpacing:0.5,fontWeight:600}}>Custom</span>
                    <button onClick={()=>removeItem(idx)} style={{...btn("danger"),padding:"3px 9px",fontSize:11,marginLeft:"auto"}}>Fjern</button>
                  </div>
                  <input style={{...inp(),marginBottom:8}} placeholder="Tittel (f.eks. Ditt skreddersydde konsept)" value={it.title} onChange={e=>updateCustom(idx,{title:e.target.value})}/>
                  <input style={{...inp(),marginBottom:8}} placeholder="Liten overskrift / eyebrow (valgfritt)" value={it.eyebrow} onChange={e=>updateCustom(idx,{eyebrow:e.target.value})}/>
                  <textarea style={{...inp(),marginBottom:8,minHeight:70,resize:"vertical",fontFamily:"inherit"}} placeholder="Beskrivelse… (tom linje = nytt avsnitt)" value={it.text} onChange={e=>updateCustom(idx,{text:e.target.value})}/>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                    <label style={{...btn("ghost"),padding:"6px 12px",fontSize:12,cursor:"pointer"}}>
                      {it._file ? `📎 ${it._file.name.slice(0,22)}` : (it.image_url ? "Bytt bilde" : "Last opp bilde")}
                      <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>updateCustom(idx,{_file:e.target.files?.[0]||null})}/>
                    </label>
                    {it.image_url && !it._file && <span style={{fontSize:11,color:C.green}}>bilde lagret</span>}
                    <input style={{...inp(),flex:"1 1 180px",width:"auto"}} placeholder="Video-URL (valgfritt, embed)" value={it.video_url} onChange={e=>updateCustom(idx,{video_url:e.target.value})}/>
                  </div>
                  {it.video_url && <div style={{fontSize:11,color:C.muted,marginTop:6}}>Med video-URL vises et video-kort. Last opp et bilde for å bruke det som thumbnail – ellers hentes thumbnail automatisk fra Vimeo/YouTube.</div>}
                </div>
              )}
            </div>
          ))}
          <button style={{...btn("ghost"),width:"100%",marginTop:14,padding:9,fontSize:13}} onClick={addCustom}>+ Legg til custom-seksjon</button>
        </div>

        <div style={{display:"flex",gap:10}}>
          <button style={{...btn("primary"),opacity:saving?0.6:1,pointerEvents:saving?"none":"auto"}} onClick={saveBuilder}>
            {saving ? "Publiserer…" : (editing ? "Lagre & publiser" : "Generer & publiser")}
          </button>
          <button style={{...btn("ghost")}} onClick={()=>setView("list")}>Avbryt</button>
        </div>
      </div>
    );
  }

  // ============================ LISTE ============================
  return (
    <div style={{paddingBottom:isMobile?80:0}}>
      <Toast msg={toast.msg} ok={toast.ok}/>
      {gradBar}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,gap:8,flexWrap:"wrap"}}>
        <div style={{fontSize:20,fontWeight:500}}>Kundeportaler</div>
        <div style={{display:"flex",gap:8}}>
          <button style={{...btn("ghost"),padding:"7px 14px",fontSize:13}} onClick={()=>setView("sections")}>Rediger seksjoner</button>
          <button style={{...btn("primary"),padding:"7px 16px",fontSize:13}} onClick={()=>openBuilder(null)}>+ Ny portal</button>
        </div>
      </div>
      <div style={{fontSize:13,color:C.muted,marginBottom:24,maxWidth:620}}>
        Bygg en skreddersydd landingsside per kunde: velg seksjoner, rekkefølge og priser, og legg til
        egne konsepter. Del lenken — siden vises i fullskjerm med videoer.
      </div>

      <div style={{maxWidth:620}}>
        {loading && <div style={{color:C.muted,fontSize:13,padding:"8px 0"}}>Laster…</div>}
        {!loading && portals.length===0 && <div style={{color:C.muted,fontSize:13,padding:"8px 0"}}>Ingen portaler ennå.</div>}
        {portals.map(p => (
          <div key={p.id} style={{...card(),marginBottom:10}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{fontSize:15,fontWeight:500,marginBottom:2}}>{p.customer}{p.config?.language==="en" && <span style={{fontSize:10,color:C.muted,marginLeft:8,padding:"1px 6px",border:`0.5px solid ${C.border}`,borderRadius:4,verticalAlign:"middle"}}>EN</span>}</div>
                <div style={{fontSize:12,color:C.blue,wordBreak:"break-all",marginBottom:4}}>{PORTAL_BASE}{p.slug}</div>
                <div style={{fontSize:11,color:C.muted}}>
                  {fmtDate(p.created_at)}
                  {p.project_id && projName(p.project_id) ? ` · ${projName(p.project_id)}` : ""}
                </div>
              </div>
            </div>
            <div style={{display:"flex",gap:8,marginTop:12,flexWrap:"wrap"}}>
              <button style={{...btn("ghost"),padding:"6px 12px",fontSize:12}} onClick={()=>openBuilder(p, p)}>Rediger</button>
              <button style={{...btn("ghost"),padding:"6px 12px",fontSize:12}} onClick={()=>openBuilder(p, null)}>Dupliser</button>
              <button style={{...btn("ghost"),padding:"6px 12px",fontSize:12}} onClick={()=>copyLink(p.slug)}>Kopier lenke</button>
              <a href={PORTAL_BASE+p.slug} target="_blank" rel="noreferrer" style={{...btn("ghost"),padding:"6px 12px",fontSize:12,textDecoration:"none",color:C.muted}}>Åpne</a>
              <button style={{...btn("danger"),padding:"6px 12px",fontSize:12}} onClick={()=>deletePortal(p)}>Slett</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
